/**
 * ChartMind-Plus landing — completions list + Start differential.
 * Independent of ChartMind (outpatient `/dashboard/chartmind`), ChartMind-I,
 * and ChartMind-I2. Day-one scope is the differential TCI only.
 */
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import { useAuth } from "hooks/useAuth";
import AiDisclosureNotice from "shared/components/aiDisclosure/AiDisclosureNotice";
import {
  DIFFERENTIAL_TCI_ID,
  isModuleCompleted,
  listChartMindPlusCompletions,
} from "./chartMindPlusSessionService";

const DIFFERENTIAL_PATH = "/chartmind-plus/differential";

export default function ChartMindPlusPage() {
  const { user } = useAuth();
  const location = useLocation();
  const isPatientSurface = !location.pathname.startsWith("/dashboard");
  const [loading, setLoading] = useState(Boolean(user));
  const [errorMessage, setErrorMessage] = useState("");
  const [completions, setCompletions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user) {
        setCompletions([]);
        setLoading(false);
        setErrorMessage("");
        return;
      }

      setLoading(true);
      setErrorMessage("");
      try {
        const done = await listChartMindPlusCompletions({ patientUid: user.uid });
        if (!cancelled) setCompletions(done);
      } catch (err) {
        if (!cancelled) {
          setCompletions([]);
          setErrorMessage(err?.message || "Failed to load ChartMind-Plus sessions.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const differentialDone = isModuleCompleted(completions, DIFFERENTIAL_TCI_ID);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        textAlign: "center",
      }}
    >
      <EditNoteRounded sx={{ fontSize: 64, mb: 3 }} color="primary" />
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        ChartMind-Plus
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 480, lineHeight: 1.6, mb: 3 }}
      >
        Confirm MasterChart context for differential diagnosis via SecureLink
        TCI session consent.
      </Typography>

      {isPatientSurface ? (
        <Box sx={{ maxWidth: 480, width: "100%", mb: 2 }}>
          <AiDisclosureNotice setting="remote" variant="banner" locale="en" />
        </Box>
      ) : null}

      {!user ? (
        <Typography variant="body2" color="text.secondary">
          Sign in to start ChartMind-Plus modules.
        </Typography>
      ) : loading ? (
        <CircularProgress />
      ) : errorMessage ? (
        <Alert severity="error" sx={{ maxWidth: 480, textAlign: "left" }}>
          {errorMessage}
        </Alert>
      ) : (
        <Box sx={{ maxWidth: 480, width: "100%", textAlign: "left" }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Recent completions
          </Typography>
          {completions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No ChartMind-Plus modules completed yet.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ mb: 3 }}>
              {completions.map((row) => (
                <ListItem
                  key={row.id}
                  disableGutters
                  secondaryAction={
                    row.status === "completed" ? (
                      <Chip size="small" color="success" label="Completed" />
                    ) : null
                  }
                  sx={{ pr: row.status === "completed" ? 12 : 0 }}
                >
                  <ListItemText
                    primary={row.tciId || "module"}
                    secondary={row.sessionId || row.id}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {differentialDone ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Differential is complete for your recent ChartMind-Plus session.
            </Alert>
          ) : null}

          <Button
            component={RouterLink}
            to={DIFFERENTIAL_PATH}
            variant="contained"
            fullWidth
          >
            {differentialDone ? "Start differential again" : "Start differential"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
